import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Output, Input, Component, NgModule } from '@angular/core';
import * as i2 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { Subject, timer } from 'rxjs';
import { filter, take, map } from 'rxjs/operators';
import { isNotNil } from '@engineering11/utility';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';

var ToastDismissType;
(function (ToastDismissType) {
    ToastDismissType["None"] = "none";
    ToastDismissType["Auto"] = "auto";
    ToastDismissType["User"] = "user";
    ToastDismissType["CtaOnly"] = "CtaOnly";
})(ToastDismissType || (ToastDismissType = {}));
const defaultToastData = {
    dismissType: ToastDismissType.None,
    dismissOnRouteChange: true,
};
/**
 * @deprecated use @IToastMessage - classes should not be used for data
 */
class E11ToastMessage {
    static fromJson(title, bodyTitle, bodyContent, type, dismissType, cta, ctaText, icon, dismissOnRouteChange) {
        return new E11ToastMessage(title, bodyTitle, bodyContent, type, dismissType, cta, ctaText, icon, dismissOnRouteChange);
    }
    constructor(title, bodyTitle, bodyContent, type = 'alert', dismissType = ToastDismissType.None, cta, ctaText, icon, dismissOnRouteChange = true) {
        this.title = title;
        this.bodyTitle = bodyTitle;
        this.bodyContent = bodyContent;
        this.type = type;
        this.dismissType = dismissType;
        this.cta = cta;
        this.ctaText = ctaText;
        this.icon = icon;
        this.dismissOnRouteChange = dismissOnRouteChange;
    }
    toJSON() {
        return { ...this };
    }
}

class E11ToastService {
    // Observable string streams
    constructor() {
        // Observable sources
        this.toastMessageSource = new Subject();
        this.callToAction$Source = new Subject();
        this.toastMessage$ = this.toastMessageSource.asObservable();
        this.callToAction$ = this.callToAction$Source.asObservable();
    }
    /**
     *  Service message commands.
     *  TODO: on breaking change, use only IToastMessage
     */
    popToastMessage(toastMessage) {
        const message = toastMessage instanceof E11ToastMessage ? toastMessage.toJSON() : toastMessage;
        const messageWithDefaults = { ...defaultToastData, ...message };
        setTimeout(() => this.toastMessageSource.next(messageWithDefaults), message.delay);
    }
    closeToast() {
        this.toastMessageSource.next(null);
    }
    // Why not?
    /**
     * @deprecated use @closeToast
     */
    burnToast() {
        this.closeToast();
    }
    /**
     * @deprecated use @closeToast
     */
    dropToast() {
        this.closeToast();
    }
    /**
     * @deprecated use @closeToast
     */
    frenchToast() {
        this.closeToast();
    }
    /**
     * @deprecated use @closeToast
     */
    takeAustinsFrenchToastAndDropItThenBurnItAndFinallyCloseTheToastHopeYouLikeLongMethodNamesBecauseThisOneIsNoJokeICanDoEasterEggsTooAustin() {
        this.closeToast();
    }
    // All of these are the same. Thought it would be funny
    initCallToAction(data) {
        this.callToAction$Source.next(data);
    }
    pullErrorMessage(text) {
        if (text.errors) {
            if (text.errors[0]) {
                return text.errors[0].title;
            }
            if (text.errors.summary) {
                return text.errors.summary[0];
            }
        }
        if (text.error) {
            return text.message;
        }
        return typeof text === 'string' ? text : 'undefined';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ToastService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ToastService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ToastService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

const resetToastDelay = 1000;
const newToastDelay = 1050; // Must be longer than resetToastDelay - otherwise new toasts do not display
/**
 * A component that displays a toast message.
 * It can display a title, body content, and a call-to-action.
 * @example
 * <e11-toast></e11-toast>
 * @example
 * <e11-toast title="Title" bodyTitle="Body Title" bodyContent="Body Content" ctaText="CTA Text" cta="action"></e11-toast>
 * @example
 * <e11-toast title="Title" bodyTitle="Body Title" bodyContent="Body Content" ctaText="CTA Text" cta="action" dismissType="Auto"></e11-toast>
 * @example
 * <e11-toast title="Title" bodyTitle="Body Title" bodyContent="Body Content" ctaText="CTA Text" cta="action" dismissType="User"></e11-toast>
 */
class E11ToastComponent {
    /**
     * Indicates if the component should close on route change.
     * @param {boolean} conditional - The condition to determine if the component should close on route change.
     */
    set closeOnRouteChange(conditional) {
        this._closeOnRouteChange = conditional !== false;
    }
    /**
     * Constructor for the component.
     * @param {E11ToastService} toasterService - The toaster service.
     * @param {Router} route - The router service.
     */
    constructor(toasterService, route) {
        this.toasterService = toasterService;
        this.route = route;
        this.dismissTypeEnum = ToastDismissType;
        this._closeOnRouteChange = false;
        /**
         * The title of the component.
         * @type {string}
         * @default ''
         */
        this.title = '';
        /**
         * Indicates if the component should close when the Escape key is pressed.
         * @type {boolean}
         * @default false
         */
        this.closeOnEscape = false;
        /**
         * Indicates if the component should close when clicking outside of it.
         * @type {boolean}
         * @default false
         */
        this.closeOnClickOutside = false;
        /**
         * Event emitted when a call-to-action is clicked.
         * @deprecated This event is deprecated. Please use {@link onCtaClicked} instead.
         * @type {EventEmitter<string>}
         */
        this.ctaClicked = new EventEmitter();
        /**
         * Event emitted when a call-to-action is clicked.
         * @type {EventEmitter<string>}
         */
        this.onCtaClicked = new EventEmitter();
        /**
         * Indicates if a toast is currently displayed.
         * @type {boolean}
         * @default false
         */
        this.toastStatus = false;
        /**
         * The count of toasts displayed.
         * @type {number}
         * @default 0
         */
        this.toastCount = 0;
        /**
         * The countdown timer for auto-dismissable toasts.
         * @type {any}
         * @default null
         */
        this.countDown = null;
        /**
         * The counter for the countdown timer.
         * @type {number}
         * @default 0
         */
        this.counter = 0;
        /**
         * The display count for the countdown timer.
         * @type {number}
         * @default 0
         */
        this.displayCount = 0;
        /**
         * Subscription to the countdown timer observable.
         * @type {Subscription}
         * @default null
         */
        this.timerSubscription = null;
    }
    ngOnInit() {
        this.appToasterObservable = this.toasterService.toastMessage$.pipe(filter(isNotNil)).subscribe((results) => {
            this.activateToast(results);
        });
        if (this._closeOnRouteChange) {
            this.route.events.pipe(filter((route) => route instanceof NavigationEnd)).subscribe(() => {
                if (this.toast && this.toast.dismissOnRouteChange) {
                    this.dismissToastNoCallback();
                }
            });
        }
    }
    /**
     * @Todo: Check this. This is older code from another component and may not work properly here
     * The requirement feels a bit odd...
     * Activates a new Toast Message.  If a Toast Message is already present on-screen
     * it will be dismissed and the new Toast Message will activate after one second. <<-- odd
     */
    // We need to 'stack' or q the toasts... make french toast... same with notifications
    /**
     * Activates a new Toast Message. If a Toast Message is already present on-screen,
     * it will be dismissed and the new Toast Message will activate after one second.
     * @param {IToastMessage} appToastMessage - The toast message to activate.
     */
    activateToast(appToastMessage) {
        if (appToastMessage) {
            if (!this.toast) {
                this.updateToast(appToastMessage);
                return;
            }
            let sameToast = true;
            for (let key of Object.keys(this.toast)) {
                let originalToast = this.toast[key]
                    ? this.toast[key].toString()
                    : this.toast[key];
                let newToast = appToastMessage[key]
                    ? appToastMessage[key].toString()
                    : appToastMessage[key];
                if (originalToast !== newToast) {
                    sameToast = false;
                }
            }
            if (sameToast === true) {
                return;
            }
            if (this.toast) {
                setTimeout(() => {
                    this.updateToast(appToastMessage);
                }, newToastDelay);
                this.dismissToastNoCallback();
            }
            else {
                this.updateToast(appToastMessage);
            }
        }
        else {
            // burn the toast
            this.dismissToast();
        }
    }
    // Applies the details from an AppToastMessage to the ToastComponent.
    // If the Toast Message is not dismissible a 5 second timer will be created and the toast will auto-dismiss when the timer completes.
    /**
     * Applies the details from an AppToastMessage to the ToastComponent.
     * If the Toast Message is not dismissible, a 5 second timer will be created and
     * the toast will auto-dismiss when the timer completes.
     * @param {IToastMessage} appToastMessage - The toast message to update.
     */
    updateToast(appToastMessage) {
        this.toast = appToastMessage;
        this.toasterService.initCallToAction('null');
        this.toastCount++;
        this.toastStatus = true;
        // if the toast is auto dismissable create a 5 second timer and dismiss the toast once the timer completes
        if (this.toast.dismissType === ToastDismissType.Auto) {
            this.counter = 6;
            this.countDown = timer(0, 1000)
                .pipe(take(this.counter))
                .pipe(map((val) => this.counter - 1 - val));
            this.timerSubscription = this.countDown.subscribe((val) => (this.displayCount = val), (_err) => { }, () => this.dismissToast());
        }
    }
    /**
     * Closer for dynamic directives.
     */
    close() {
        this.dismissToast();
    }
    /**
     * Dismisses a Toast Message. If the Toast Message has a callback, it will be invoked.
     */
    dismissToast() {
        if (this.toast && this.toast.callback) {
            this.toast.callback();
        }
        this.resetToastValues();
    }
    /**
     * Dismisses a Toast Message. If the Toast Message has a callback function,
     * it will not be invoked.
     */
    dismissToastNoCallback() {
        if (this.toast) {
            this.resetToastValues();
        }
    }
    /**
     * Resets the Toast Component's properties. If the toast has a timer and
     * was dismissed before the timer completed, unsubscribe from observable timer.
     */
    resetToastValues() {
        this.timerSubscription ? this.timerSubscription.unsubscribe() : null;
        this.toastStatus = false;
        this.countDown = null;
        this.counter = 0;
        setTimeout(() => {
            this.toast = null;
        }, resetToastDelay);
    }
    /**
     * Handles call-to-action click events.
     * @param {string} action - The action performed.
     */
    cta(action) {
        if (this.toast?.dismissType !== ToastDismissType.None) {
            this.dismissToast();
        }
        this.onCtaClicked.emit(action);
        // TODO - Remove deprecated event in next major release
        this.ctaClicked.emit(action);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ToastComponent, deps: [{ token: E11ToastService }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11ToastComponent, isStandalone: true, selector: "e11-toast", inputs: { title: "title", closeOnEscape: "closeOnEscape", closeOnClickOutside: "closeOnClickOutside", closeOnRouteChange: "closeOnRouteChange" }, outputs: { ctaClicked: "ctaClicked", onCtaClicked: "onCtaClicked" }, ngImport: i0, template: `
    <div
      class="e11-toasts e11-fixed e11-z-[1004] e11--bottom-52 e11-rounded-md e11-bg-skin-primary-accent e11-opacity-95"
      [ngClass]="{ active: toastStatus }"
    >
      <div class="e11-toast e11-relative e11-items-center e11-min-h-20 e11-px-6 e11-pt-6 e11-pb-12" *ngIf="toast" [ngClass]="toast.type">
        <div class="e11-h-full">
          <div class="e11-w-full e11-items-center">
            <div class="e11-flex">
              <div class="e11-grow">
                <h3 class="e11-font-bold e11-m-0 e11-text-white">{{ toast.title }}</h3>
              </div>
              <div class="dismiss-container e11-h-10">
                <div
                  *ngIf="toast.dismissType === dismissTypeEnum.Auto && displayCount >= 1"
                  class="countdown e11-w-10 e11-h-10 e11-m-0 e11-ml-auto e11-cursor-pointer"
                  (click)="dismissToast()"
                >
                  <div
                    class="countdown-number e11-flex e11-items-center e11-justify-center e11-h-10 e11-absolute e11-w-10 e11-mx-0 e11-my-auto e11-text-white e11-font-bold e11-leading-10 e11-text-center"
                    [ngClass]="{ active: toastStatus }"
                  >
                    <span class="material-icons-outlined"> close </span>
                  </div>
                  <svg class="e11-w-10 e11-h-10">
                    <circle r="18" cx="20" cy="20" [ngClass]="{ active: toastStatus }"></circle>
                  </svg>
                </div>
                <!-- This should only show if there is a CTA or callback -->
                <p
                  *ngIf="toast.dismissType === dismissTypeEnum.User"
                  class="e11-inline-flex e11-items-center e11-h-6 e11-text-white e11-text-sm e11-font-bold e11-cursor-pointer e11-mb-0"
                  (click)="dismissToast()"
                >
                  {{ toast.dismissText ?? 'Dismiss' }}
                  <span
                    class="material-icons md-24 e11-align-bottom e11-rounded-full e11-w-6 e11-h-6 hover:e11-border hover:e11-border-skin-white e11-flex e11-items-center e11-justify-center e11-ml-1"
                    >keyboard_arrow_down</span
                  >
                </p>
              </div>
            </div>
            <div class="body e11-flex e11-items-center">
              <div class="e11-text-skin-white e11-mr-2">
                <span class="material-icons md-48">{{ toast.icon }}</span>
              </div>
              <div class="e11-grow">
                <p class="e11-font-black e11-m-0 e11-text-white" *ngIf="toast.bodyTitle.trim()">{{ toast.bodyTitle }}</p>
                <p class="e11-m-0 e11-text-white" *ngIf="toast.bodyContent?.trim()">{{ toast.bodyContent }}</p>
              </div>
              <div class="e11-px-2" *ngIf="toast.cta">
                <div
                  class="e11-bg-skin-primary-accent hover:e11-bg-skin-primary e11-text-sm e11-px-6 e11-py-2 e11-border e11-rounded-full e11-border-white e11-text-white e11-cursor-pointer"
                  (click)="cta(toast.cta)"
                >
                  {{ toast.ctaText }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `, isInline: true, styles: [".e11-toasts{width:calc(100% - 2rem);transition:bottom .75s ease-in-out 0s;left:1rem}.e11-toasts .e11-toast .countdown-number.active{animation:fadeout 5s linear infinite forwards}.e11-toasts .e11-toast svg{transform:rotateY(-180deg) rotate(-90deg)}.e11-toasts .e11-toast svg circle{stroke-width:2px;stroke-linecap:round;stroke-dasharray:113px;stroke-dashoffset:0;stroke:#fff;fill:none}.e11-toasts .e11-toast svg circle.active{animation:countdown 5s linear infinite forwards,fadeout 5s linear infinite forwards}@keyframes countdown{0%{stroke-dashoffset:0}to{stroke-dashoffset:113px}}@keyframes fadeout{0%{opacity:1}75%{opacity:1}to{opacity:0}}.e11-toasts.active{bottom:1rem;transition:bottom .25s ease-in-out 0s}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ToastComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-toast', standalone: true, imports: [CommonModule], template: `
    <div
      class="e11-toasts e11-fixed e11-z-[1004] e11--bottom-52 e11-rounded-md e11-bg-skin-primary-accent e11-opacity-95"
      [ngClass]="{ active: toastStatus }"
    >
      <div class="e11-toast e11-relative e11-items-center e11-min-h-20 e11-px-6 e11-pt-6 e11-pb-12" *ngIf="toast" [ngClass]="toast.type">
        <div class="e11-h-full">
          <div class="e11-w-full e11-items-center">
            <div class="e11-flex">
              <div class="e11-grow">
                <h3 class="e11-font-bold e11-m-0 e11-text-white">{{ toast.title }}</h3>
              </div>
              <div class="dismiss-container e11-h-10">
                <div
                  *ngIf="toast.dismissType === dismissTypeEnum.Auto && displayCount >= 1"
                  class="countdown e11-w-10 e11-h-10 e11-m-0 e11-ml-auto e11-cursor-pointer"
                  (click)="dismissToast()"
                >
                  <div
                    class="countdown-number e11-flex e11-items-center e11-justify-center e11-h-10 e11-absolute e11-w-10 e11-mx-0 e11-my-auto e11-text-white e11-font-bold e11-leading-10 e11-text-center"
                    [ngClass]="{ active: toastStatus }"
                  >
                    <span class="material-icons-outlined"> close </span>
                  </div>
                  <svg class="e11-w-10 e11-h-10">
                    <circle r="18" cx="20" cy="20" [ngClass]="{ active: toastStatus }"></circle>
                  </svg>
                </div>
                <!-- This should only show if there is a CTA or callback -->
                <p
                  *ngIf="toast.dismissType === dismissTypeEnum.User"
                  class="e11-inline-flex e11-items-center e11-h-6 e11-text-white e11-text-sm e11-font-bold e11-cursor-pointer e11-mb-0"
                  (click)="dismissToast()"
                >
                  {{ toast.dismissText ?? 'Dismiss' }}
                  <span
                    class="material-icons md-24 e11-align-bottom e11-rounded-full e11-w-6 e11-h-6 hover:e11-border hover:e11-border-skin-white e11-flex e11-items-center e11-justify-center e11-ml-1"
                    >keyboard_arrow_down</span
                  >
                </p>
              </div>
            </div>
            <div class="body e11-flex e11-items-center">
              <div class="e11-text-skin-white e11-mr-2">
                <span class="material-icons md-48">{{ toast.icon }}</span>
              </div>
              <div class="e11-grow">
                <p class="e11-font-black e11-m-0 e11-text-white" *ngIf="toast.bodyTitle.trim()">{{ toast.bodyTitle }}</p>
                <p class="e11-m-0 e11-text-white" *ngIf="toast.bodyContent?.trim()">{{ toast.bodyContent }}</p>
              </div>
              <div class="e11-px-2" *ngIf="toast.cta">
                <div
                  class="e11-bg-skin-primary-accent hover:e11-bg-skin-primary e11-text-sm e11-px-6 e11-py-2 e11-border e11-rounded-full e11-border-white e11-text-white e11-cursor-pointer"
                  (click)="cta(toast.cta)"
                >
                  {{ toast.ctaText }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `, styles: [".e11-toasts{width:calc(100% - 2rem);transition:bottom .75s ease-in-out 0s;left:1rem}.e11-toasts .e11-toast .countdown-number.active{animation:fadeout 5s linear infinite forwards}.e11-toasts .e11-toast svg{transform:rotateY(-180deg) rotate(-90deg)}.e11-toasts .e11-toast svg circle{stroke-width:2px;stroke-linecap:round;stroke-dasharray:113px;stroke-dashoffset:0;stroke:#fff;fill:none}.e11-toasts .e11-toast svg circle.active{animation:countdown 5s linear infinite forwards,fadeout 5s linear infinite forwards}@keyframes countdown{0%{stroke-dashoffset:0}to{stroke-dashoffset:113px}}@keyframes fadeout{0%{opacity:1}75%{opacity:1}to{opacity:0}}.e11-toasts.active{bottom:1rem;transition:bottom .25s ease-in-out 0s}\n"] }]
        }], ctorParameters: () => [{ type: E11ToastService }, { type: i2.Router }], propDecorators: { title: [{
                type: Input
            }], closeOnEscape: [{
                type: Input
            }], closeOnClickOutside: [{
                type: Input
            }], closeOnRouteChange: [{
                type: Input
            }], ctaClicked: [{
                type: Output
            }], onCtaClicked: [{
                type: Output
            }] } });

class E11ToastModule {
    static forRoot() {
        return {
            ngModule: E11ToastModule,
            providers: [], // Be extremely careful about declaring services in providers arrays - it can prevent them from being singletons
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ToastModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11ToastModule, imports: [E11ToastComponent], exports: [E11ToastComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ToastModule, imports: [E11ToastComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ToastModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [E11ToastComponent],
                    exports: [E11ToastComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11ToastComponent, E11ToastMessage, E11ToastModule, E11ToastService, ToastDismissType, defaultToastData };
